import { useEffect, useId, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { Code } from "@connectrpc/connect";
import { useMutation, useQuery } from "@connectrpc/connect-query";
import { skipToken } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { formatNumber, toAsciiDigits } from "@yatayat/core/money";
import { ComplaintCategory, ComplaintService } from "@yatayat/gen/yatayat/complaints/v1/complaints_pb";
import { RegistryService } from "@yatayat/gen/yatayat/registry/v1/registry_pb";

import { normalizeStickerCode, stickerCodePattern } from "~/components/VehicleStickerForm";
import { complaintCategoryLabels } from "~/lib/complaint-labels";
import { useI18n, type MessageKey } from "~/lib/i18n";
import { PhotoError, shrinkPhoto, type ShrunkPhoto } from "~/lib/photo";
import { errorCode, retryTransient } from "~/lib/rpc/errors";
import { searchString } from "~/lib/search";
import { ui } from "~/lib/ui";

/** What the complaint is about, as the vehicle page, ticket page or a trip links here. */
type ComplaintAbout = { sticker?: string; ticket?: string; trip?: string };

export const Route = createFileRoute("/complaints/new")({
  validateSearch: (search: Record<string, unknown>): ComplaintAbout => ({
    sticker: searchString(search.sticker),
    ticket: searchString(search.ticket),
    trip: searchString(search.trip),
  }),
  component: NewComplaint,
});

function NewComplaint() {
  const search = Route.useSearch();

  // A different reference in the URL starts the form afresh.
  return <ComplaintForm key={`${search.sticker}|${search.ticket}|${search.trip}`} search={search} />;
}

const categories = [
  ComplaintCategory.OVERCHARGING,
  ComplaintCategory.REFUSED_TICKET,
  ComplaintCategory.UNSAFE_DRIVING,
  ComplaintCategory.HARASSMENT,
  ComplaintCategory.VEHICLE_CONDITION,
  ComplaintCategory.DELAY_OR_CANCELLATION,
  ComplaintCategory.OTHER,
];

// The api's limits on a description, counted in characters.
const minDescription = 10;
const maxDescription = 2000;

const ticketCodePattern = /^YT-[A-Z0-9]{6}$/;
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** Uppercases a typed ticket code, drops spaces and adds the YT- prefix when it's missing. */
function normalizeTicketCode(s: string): string {
  const compact = toAsciiDigits(s).toUpperCase().replace(/\s/g, "");
  return /^[A-Z0-9]{6}$/.test(compact) ? `YT-${compact}` : compact.replace(/^YT(?=[A-Z0-9]{6}$)/, "YT-");
}

/** Keeps the well-formed references from the URL; null when none is. */
function aboutFromSearch(search: ComplaintAbout): ComplaintAbout | null {
  const sticker = normalizeStickerCode(search.sticker ?? "");
  const ticket = normalizeTicketCode(search.ticket ?? "");
  const trip = (search.trip ?? "").trim().toLowerCase();
  const about: ComplaintAbout = {
    sticker: stickerCodePattern.test(sticker) ? sticker : undefined,
    ticket: ticketCodePattern.test(ticket) ? ticket : undefined,
    trip: uuidPattern.test(trip) ? trip : undefined,
  };

  return about.sticker || about.ticket || about.trip ? about : null;
}

type Field = "category" | "about" | "sticker" | "ticket" | "description";
type Located = { lat: number; lng: number; accuracy: number };

// The api accepts locations inside this box around Nepal.
const inNepal = (lat: number, lng: number) => lat >= 26 && lat <= 31 && lng >= 80 && lng <= 89;

const inputClass = "w-full rounded-md border border-slate-300 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-900";
const secondaryClass =
  "rounded-md border border-slate-300 px-4 py-2 text-sm hover:bg-slate-100 disabled:opacity-60 dark:border-slate-700 dark:hover:bg-slate-900";
const linkClass = "text-sm text-ink underline-offset-2 hover:underline dark:text-ink-dark";
const errorClass = "mt-1 text-sm text-brand dark:text-brand-dark";

function ComplaintForm({ search }: { search: ComplaintAbout }) {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);
  const id = useId();
  const ids = {
    sticker: `${id}-sticker`,
    ticket: `${id}-ticket`,
    about: `${id}-about`,
    aboutHint: `${id}-about-hint`,
    category: `${id}-category`,
    description: `${id}-description`,
    descriptionHint: `${id}-description-hint`,
    descriptionError: `${id}-description-error`,
    stickerError: `${id}-sticker-error`,
    ticketError: `${id}-ticket-error`,
    locationHint: `${id}-location-hint`,
    photoHint: `${id}-photo-hint`,
  };

  const [category, setCategory] = useState<ComplaintCategory | null>(null);
  const [given, setGiven] = useState(() => aboutFromSearch(search));
  const [stickerInput, setStickerInput] = useState("");
  const [ticketInput, setTicketInput] = useState("");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState<Located | null>(null);
  const [locating, setLocating] = useState(false);
  const [locationError, setLocationError] = useState<MessageKey | null>(null);
  const [photo, setPhoto] = useState<ShrunkPhoto | null>(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoError, setPhotoError] = useState<MessageKey | null>(null);
  const [errors, setErrors] = useState<Partial<Record<Field, MessageKey>>>({});
  const [submitError, setSubmitError] = useState<MessageKey | null>(null);
  const [focusTick, setFocusTick] = useState(0);

  const previewUrl = useObjectUrl(photo?.blob);
  const submit = useMutation(ComplaintService.method.submitComplaint);
  // Usually already cached from the vehicle page; confirms which vehicle this is about.
  const vehicle = useQuery(
    RegistryService.method.verifyVehicle,
    given?.sticker ? { stickerCode: given.sticker } : skipToken,
    { retry: retryTransient },
  );

  useEffect(() => {
    if (focusTick > 0) formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
  }, [focusTick]);

  function clearError(...fields: Field[]) {
    setErrors((e) => (fields.some((f) => e[f]) ? { ...e, ...Object.fromEntries(fields.map((f) => [f, undefined])) } : e));
  }

  function changeAbout() {
    setStickerInput(given?.sticker ?? "");
    setTicketInput(given?.ticket ?? "");
    setGiven(null);
  }

  function shareLocation() {
    setLocationError(null);
    if (!window.isSecureContext) {
      setLocationError("locationInsecure");
      return;
    }
    if (!("geolocation" in navigator)) {
      setLocationError("locationUnavailable");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        const { latitude: lat, longitude: lng, accuracy } = pos.coords;
        if (!inNepal(lat, lng)) {
          setLocationError("locationOutsideNepal");
          return;
        }
        setLocation({ lat, lng, accuracy });
      },
      (err) => {
        setLocating(false);
        setLocationError(err.code === err.PERMISSION_DENIED ? "locationDenied" : "locationUnavailable");
      },
      { enableHighAccuracy: true, timeout: 20_000, maximumAge: 60_000 },
    );
  }

  async function pickPhoto(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Lets the same file be picked again after removing it.
    e.target.value = "";
    if (!file) return;
    setPhotoError(null);
    setPhotoBusy(true);
    try {
      setPhoto(await shrinkPhoto(file));
    } catch (err) {
      setPhotoError(err instanceof PhotoError && err.problem === "tooLarge" ? "photoTooLarge" : "photoUnreadable");
    } finally {
      setPhotoBusy(false);
    }
  }

  function send(e: FormEvent) {
    e.preventDefault();
    setSubmitError(null);

    const found: Partial<Record<Field, MessageKey>> = {};
    if (category === null) found.category = "chooseCategory";

    let about: ComplaintAbout;
    if (given) {
      about = given;
    } else {
      const sticker = normalizeStickerCode(stickerInput);
      const ticket = normalizeTicketCode(ticketInput);
      if (sticker !== "" && !stickerCodePattern.test(sticker)) found.sticker = "stickerCodeInvalid";
      if (ticket !== "" && !ticketCodePattern.test(ticket)) found.ticket = "ticketCodeInvalid";
      if (sticker === "" && ticket === "") found.about = "needAbout";
      about = { sticker: sticker || undefined, ticket: ticket || undefined };
    }

    const text = description.trim();
    if ([...text].length < minDescription) found.description = "descriptionTooShort";

    setErrors(found);
    if (category === null || Object.keys(found).length > 0) {
      setFocusTick((n) => n + 1);
      return;
    }

    submit.mutate(
      {
        category,
        description: text,
        stickerCode: about.sticker ?? "",
        ticketCode: about.ticket ?? "",
        tripId: about.trip ?? "",
        location: location ? { lat: location.lat, lng: location.lng } : undefined,
        photo: photo ? { contentType: photo.contentType, data: photo.data } : undefined,
      },
      {
        onSuccess: (res) =>
          void navigate({
            to: "/complaints/$trackingNo",
            params: { trackingNo: res.trackingNo },
            search: { filed: true },
            // Back then skips the sent form.
            replace: true,
          }),
        onError: (err) => setSubmitError(submitErrorKey(err)),
      },
    );
  }

  const vehicleLine = vehicle.data
    ? [vehicle.data.plateNo, vehicle.data.operatorName?.[lang]].filter(Boolean).join(" · ")
    : null;
  const descriptionLength = [...description.trim()].length;

  return (
    <div className="mx-auto max-w-xl space-y-6">
      <div>
        <h1 className={ui.h1}>{t("reportProblem")}</h1>
        <p className={ui.lead}>{t("complaintIntro")}</p>
      </div>

      <form ref={formRef} className="space-y-6" onSubmit={send} noValidate>
        <fieldset>
          <legend className="mb-2 font-medium">{t("complaintWhatHappened")}</legend>
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <label
                key={c}
                className={`cursor-pointer rounded-md border px-3 py-2 text-sm has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ink ${
                  category === c
                    ? "border-ink bg-ink text-white dark:border-ink-dark dark:bg-ink-dark dark:text-slate-950"
                    : "border-slate-300 dark:border-slate-700"
                }`}
              >
                <input
                  type="radio"
                  name="category"
                  className="sr-only"
                  checked={category === c}
                  onChange={() => {
                    setCategory(c);
                    clearError("category");
                  }}
                  aria-invalid={errors.category ? true : undefined}
                  aria-describedby={errors.category ? ids.category : undefined}
                />
                {t(complaintCategoryLabels[c])}
              </label>
            ))}
          </div>
          {errors.category && (
            <p id={ids.category} className={errorClass}>
              {t(errors.category)}
            </p>
          )}
        </fieldset>

        <fieldset>
          <legend className="mb-2 font-medium">{t("complaintAbout")}</legend>
          {given ? (
            <div className="flex items-start justify-between gap-3 rounded-lg border border-slate-200 p-3 dark:border-slate-800">
              <dl className="min-w-0 space-y-2 text-sm">
                {given.sticker && (
                  <div>
                    <dt className="text-slate-600 dark:text-slate-400">{t("aboutSticker")}</dt>
                    <dd className="font-mono font-medium">{given.sticker}</dd>
                    {vehicleLine && <dd className="break-words">{vehicleLine}</dd>}
                    {vehicle.isError && errorCode(vehicle.error) === Code.NotFound && (
                      <dd className="text-brand dark:text-brand-dark">{t("stickerNotFound")}</dd>
                    )}
                  </div>
                )}
                {given.ticket && (
                  <div>
                    <dt className="text-slate-600 dark:text-slate-400">{t("ticketCode")}</dt>
                    <dd className="font-mono font-medium">{given.ticket}</dd>
                  </div>
                )}
                {given.trip && (
                  <div>
                    <dt className="text-slate-600 dark:text-slate-400">{t("aboutTrip")}</dt>
                    <dd className="break-all font-mono text-xs">{given.trip}</dd>
                  </div>
                )}
              </dl>
              <button type="button" onClick={changeAbout} className={`shrink-0 ${linkClass}`}>
                {t("changeAbout")}
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <p id={ids.aboutHint} className="text-sm text-slate-600 dark:text-slate-400">
                {t("aboutHint")}
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label htmlFor={ids.sticker} className="mb-1 block text-sm font-medium">
                    {t("stickerCodeLabel")}
                  </label>
                  <input
                    id={ids.sticker}
                    value={stickerInput}
                    onChange={(e) => {
                      setStickerInput(e.target.value.toUpperCase());
                      clearError("sticker", "about");
                    }}
                    autoComplete="off"
                    autoCapitalize="characters"
                    autoCorrect="off"
                    spellCheck={false}
                    maxLength={12}
                    aria-invalid={errors.sticker || errors.about ? true : undefined}
                    aria-describedby={
                      errors.sticker ? ids.stickerError : errors.about ? `${ids.aboutHint} ${ids.about}` : ids.aboutHint
                    }
                    className={`${inputClass} font-mono uppercase tracking-wider`}
                  />
                  {errors.sticker && (
                    <p id={ids.stickerError} className={errorClass}>
                      {t(errors.sticker)}
                    </p>
                  )}
                </div>
                <div>
                  <label htmlFor={ids.ticket} className="mb-1 block text-sm font-medium">
                    {t("ticketCode")}
                  </label>
                  <input
                    id={ids.ticket}
                    value={ticketInput}
                    onChange={(e) => {
                      setTicketInput(e.target.value.toUpperCase());
                      clearError("ticket", "about");
                    }}
                    placeholder={t("ticketCodeHint")}
                    autoComplete="off"
                    autoCapitalize="characters"
                    autoCorrect="off"
                    spellCheck={false}
                    maxLength={12}
                    aria-invalid={errors.ticket ? true : undefined}
                    aria-describedby={errors.ticket ? ids.ticketError : ids.aboutHint}
                    className={`${inputClass} font-mono uppercase tracking-wider placeholder:normal-case placeholder:tracking-normal`}
                  />
                  {errors.ticket && (
                    <p id={ids.ticketError} className={errorClass}>
                      {t(errors.ticket)}
                    </p>
                  )}
                </div>
              </div>
              {errors.about && (
                <p id={ids.about} className={errorClass}>
                  {t(errors.about)}
                </p>
              )}
            </div>
          )}
        </fieldset>

        <div>
          <label htmlFor={ids.description} className="mb-1 block font-medium">
            {t("complaintDescription")}
          </label>
          <textarea
            id={ids.description}
            rows={5}
            maxLength={maxDescription}
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              clearError("description");
            }}
            aria-invalid={errors.description ? true : undefined}
            aria-describedby={errors.description ? `${ids.descriptionHint} ${ids.descriptionError}` : ids.descriptionHint}
            className={inputClass}
          />
          <div className="mt-1 flex justify-between gap-3 text-xs text-slate-500 dark:text-slate-400">
            <span id={ids.descriptionHint}>{t("complaintDescriptionHint")}</span>
            <span className="shrink-0 tabular-nums">
              {t("charCount", { n: formatNumber(descriptionLength, lang), max: formatNumber(maxDescription, lang) })}
            </span>
          </div>
          {errors.description && (
            <p id={ids.descriptionError} className={errorClass}>
              {t(errors.description)}
            </p>
          )}
        </div>

        <section className="space-y-2">
          <h2 className="font-medium">{t("locationLabel")}</h2>
          {location ? (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm dark:border-emerald-900 dark:bg-emerald-900/20">
              <span className="text-emerald-800 dark:text-emerald-300">
                <span aria-hidden="true">✓ </span>
                {t("locationAttached", { m: formatNumber(Math.max(1, Math.round(location.accuracy)), lang, { maxFraction: 0 }) })}
              </span>
              <button type="button" onClick={() => setLocation(null)} className={secondaryClass}>
                {t("removeLocation")}
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={shareLocation}
              disabled={locating}
              aria-describedby={ids.locationHint}
              className={secondaryClass}
            >
              {locating ? t("locating") : t("shareLocation")}
            </button>
          )}
          <p id={ids.locationHint} className="text-xs text-slate-500 dark:text-slate-400">
            {t("locationHint")}
          </p>
          {locationError && (
            <p role="alert" className="text-sm text-brand dark:text-brand-dark">
              {t(locationError)}
            </p>
          )}
        </section>

        <section className="space-y-2">
          <h2 className="font-medium">{t("photoLabel")}</h2>
          {photo && previewUrl ? (
            <div className="flex flex-wrap items-start gap-3">
              <img
                src={previewUrl}
                alt={t("photoAlt")}
                className="max-h-48 max-w-full rounded-lg border border-slate-200 object-contain dark:border-slate-800"
              />
              <button type="button" onClick={() => setPhoto(null)} className={secondaryClass}>
                {t("removePhoto")}
              </button>
            </div>
          ) : (
            <label
              className={`inline-block cursor-pointer has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ink ${secondaryClass} ${
                photoBusy ? "pointer-events-none opacity-60" : ""
              }`}
            >
              <input
                type="file"
                accept="image/*"
                className="sr-only"
                disabled={photoBusy}
                onChange={(e) => void pickPhoto(e)}
                aria-describedby={ids.photoHint}
              />
              {photoBusy ? t("photoPreparing") : t("addPhoto")}
            </label>
          )}
          <p id={ids.photoHint} className="text-xs text-slate-500 dark:text-slate-400">
            {t("photoHint")}
          </p>
          {photoError && (
            <p role="alert" className="text-sm text-brand dark:text-brand-dark">
              {t(photoError)}
            </p>
          )}
        </section>

        <div className="space-y-3">
          {submitError && (
            <p role="alert" className="text-sm text-brand dark:text-brand-dark">
              {t(submitError)}
            </p>
          )}
          <button
            type="submit"
            disabled={submit.isPending || photoBusy || locating}
            className="w-full rounded-lg bg-ink px-4 py-3 font-semibold text-white disabled:opacity-60 sm:w-auto dark:bg-ink-dark dark:text-slate-950"
          >
            {submit.isPending ? t("sendingComplaint") : t("sendComplaint")}
          </button>
        </div>
      </form>
    </div>
  );
}

function submitErrorKey(err: unknown): MessageKey {
  switch (errorCode(err)) {
    case Code.InvalidArgument:
      return "complaintInvalid";
    case Code.NotFound:
      return "complaintRefNotFound";
    case Code.ResourceExhausted:
      return "complaintTooMany";
    default:
      return "somethingWrong";
  }
}

/** An object URL for a blob, revoked when the blob changes or the component unmounts. */
function useObjectUrl(blob: Blob | undefined): string | undefined {
  const [url, setUrl] = useState<string>();

  useEffect(() => {
    if (!blob) {
      setUrl(undefined);
      return;
    }
    const next = URL.createObjectURL(blob);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [blob]);

  return url;
}
